import { useEffect, useMemo } from 'react'
import { AdditiveBlending, Color, FrontSide, NoColorSpace, ShaderMaterial, SRGBColorSpace, Texture, Vector2 } from 'three'
import {
    endCardBackgroundOffsetX,
    endCardBackgroundOffsetYA,
    endCardBackgroundScreenScale,
    endCardStreaksDensity,
    endCardStreaksEdgeFadeEnd,
    endCardStreaksEdgeFadeStart,
    endCardStreaksEdgeNoise,
    endCardStreaksGradientMid,
    endCardStreaksGradientOuterStart,
    endCardStreaksSpeed,
    endCardStreaksWidthCenter,
    endCardStreaksWidthEdge,
} from '@/src/game/config/constants'
import { textureAssetCompressed } from '../../assetStore'
import { useCompressedTexture } from '../../ktx2Textures'

export type EndCardShaderMaterials = {
    backgroundMaterials: Record<EndCardBackgroundVariant, ShaderMaterial>
    backgroundMaterial: ShaderMaterial
    frame1Material: ShaderMaterial
    frame2Materials: Record<EndCardBackgroundVariant, ShaderMaterial>
    frame2Material: ShaderMaterial
    streaksMaterials: Record<EndCardBackgroundVariant, ShaderMaterial>
    voidMaterial: ShaderMaterial
    streaksMaterial: ShaderMaterial
}

export type EndCardBackgroundVariant = 'battery' | 'coin' | 'shield' | 'stars'

type EndCardStreaksPalette = {
    inner: Color
    middle: Color
    outer: Color
}

const endCardStreaksPalettes: Record<EndCardBackgroundVariant, EndCardStreaksPalette> = {
    battery: {
        inner: new Color(0.0, 0.3, 0.0),
        middle: new Color(0.0, 0.8, 0.0),
        outer: new Color(0.0, 0.9, 0.0),
    },
    coin: {
        inner: new Color(1, 0.7, 0.0),
        middle: new Color(1, 0.9, 0.0),
        outer: new Color(1.0, 1.0, 0.0),
    },
    shield: {
        inner: new Color(0.04, 0.1, 0.72),
        middle: new Color(0.08, 0.2, 0.96),
        outer: new Color(0.2, 0.8, 1.0),
    },
    stars: {
        inner: new Color(0.02, 0.14, 0.72),
        middle: new Color(0.35, 0.05, 0.82),
        outer: new Color(1.0, 0.22, 0.72),
    },
}

function configureTexture(texture: Texture, isColorTexture = true) {
    texture.colorSpace = isColorTexture ? SRGBColorSpace : NoColorSpace
    texture.flipY = true
}

export function useEndCardShaderMaterials(): EndCardShaderMaterials {
    const backgroundBatteryTexture = useCompressedTexture(textureAssetCompressed.EndCardBackgroundBattery)
    const backgroundCoinTexture = useCompressedTexture(textureAssetCompressed.EndCardBackgroundCoin)
    const backgroundShieldTexture = useCompressedTexture(textureAssetCompressed.EndCardBackgroundShield)
    const backgroundStarsTexture = useCompressedTexture(textureAssetCompressed.EndCardBackgroundStars)
    const frame1Texture = useCompressedTexture(textureAssetCompressed.EndCardFrame1)
    const frame2BatteryTexture = useCompressedTexture(textureAssetCompressed.EndCardFrame2Battery)
    const frame2CoinTexture = useCompressedTexture(textureAssetCompressed.EndCardFrame2Coin)
    const frame2ShieldTexture = useCompressedTexture(textureAssetCompressed.EndCardFrame2Shield)
    const frame2StarsTexture = useCompressedTexture(textureAssetCompressed.EndCardFrame2Stars)
    const normalTexture = useCompressedTexture(textureAssetCompressed.EndCardFrameNormal)

    configureTexture(backgroundBatteryTexture)
    configureTexture(backgroundCoinTexture)
    configureTexture(backgroundShieldTexture)
    configureTexture(backgroundStarsTexture)
    configureTexture(frame1Texture)
    configureTexture(frame2BatteryTexture)
    configureTexture(frame2CoinTexture)
    configureTexture(frame2ShieldTexture)
    configureTexture(frame2StarsTexture)
    configureTexture(normalTexture, false)

    const materials = useMemo(() => {
        const backgroundMaterials = {
            battery: createEndCardBackgroundMaterial(backgroundBatteryTexture),
            coin: createEndCardBackgroundMaterial(backgroundCoinTexture),
            shield: createEndCardBackgroundMaterial(backgroundShieldTexture),
            stars: createEndCardBackgroundMaterial(backgroundStarsTexture),
        } satisfies Record<EndCardBackgroundVariant, ShaderMaterial>
        const streaksMaterials = {
            battery: createEndCardStreaksMaterial(endCardStreaksPalettes.battery),
            coin: createEndCardStreaksMaterial(endCardStreaksPalettes.coin),
            shield: createEndCardStreaksMaterial(endCardStreaksPalettes.shield),
            stars: createEndCardStreaksMaterial(endCardStreaksPalettes.stars),
        } satisfies Record<EndCardBackgroundVariant, ShaderMaterial>
        const frame2Materials = {
            battery: createEndCardFrameMaterial(frame2BatteryTexture, normalTexture, new Vector2(0.65, -0.48)),
            coin: createEndCardFrameMaterial(frame2CoinTexture, normalTexture, new Vector2(0.65, -0.48)),
            shield: createEndCardFrameMaterial(frame2ShieldTexture, normalTexture, new Vector2(0.65, -0.48)),
            stars: createEndCardFrameMaterial(frame2StarsTexture, normalTexture, new Vector2(0.65, -0.48)),
        } satisfies Record<EndCardBackgroundVariant, ShaderMaterial>

        return {
            backgroundMaterials,
            backgroundMaterial: backgroundMaterials.stars,
            frame1Material: createEndCardFrameMaterial(frame1Texture, normalTexture),
            frame2Materials,
            frame2Material: frame2Materials.stars,
            streaksMaterials,
            voidMaterial: createEndCardVoidMaterial(),
            streaksMaterial: streaksMaterials.coin,
        }
    }, [
        backgroundBatteryTexture,
        backgroundCoinTexture,
        backgroundShieldTexture,
        backgroundStarsTexture,
        frame1Texture,
        frame2BatteryTexture,
        frame2CoinTexture,
        frame2ShieldTexture,
        frame2StarsTexture,
        normalTexture,
    ])

    useEffect(() => {
        return () => {
            Object.values(materials.backgroundMaterials).forEach((material) => material.dispose())
            materials.frame1Material.dispose()
            Object.values(materials.frame2Materials).forEach((material) => material.dispose())
            Object.values(materials.streaksMaterials).forEach((material) => material.dispose())
            materials.voidMaterial.dispose()
        }
    }, [materials])

    return materials
}

export function createEndCardVoidMaterial() {
    return new ShaderMaterial({
        side: FrontSide,
        transparent: true,
        toneMapped: false,
        uniforms: {
            uResolution: { value: new Vector2(1, 1) },
            uTime: { value: 0 },
            uOpacity: { value: 1 },
        },
        vertexShader: `
            void main() {
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform vec2 uResolution;
            uniform float uTime;
            uniform float uOpacity;
            uniform float uSpeed;
            uniform float uDensity;
            uniform float uWidthCenter;
            uniform float uWidthEdge;
            uniform float uEdgeFadeStart;
            uniform float uEdgeFadeEnd;
            uniform float uEdgeNoise;
            uniform float uGradientMid;
            uniform float uGradientOuterStart;

            float hash21(vec2 p) {
                p = fract(p * vec2(123.34, 456.21));
                p += dot(p, p + 45.32);
                return fract(p.x * p.y);
            }

            float valueNoise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                vec2 u = f * f * (3.0 - 2.0 * f);

                float a = hash21(i);
                float b = hash21(i + vec2(1.0, 0.0));
                float c = hash21(i + vec2(0.0, 1.0));
                float d = hash21(i + vec2(1.0, 1.0));

                return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
            }

            mat2 rotate2D(float angle) {
                float s = sin(angle);
                float c = cos(angle);
                return mat2(c, -s, s, c);
            }

            float fbm(vec2 p) {
                float value = 0.0;
                float amplitude = 0.5;
                for (int i = 0; i < 4; i++) {
                    value += valueNoise(p) * amplitude;
                    p = rotate2D(0.45) * p * 2.0 + vec2(4.8, 2.1);
                    amplitude *= 0.5;
                }
                return value;
            }

            float starSparkle(vec2 starUv) {
                vec2 p = abs(starUv);
                float starShape = max(p.x, p.y) + min(p.x, p.y) * 2.8;
                float core = 1.0 - smoothstep(0.018, 0.11, starShape);
                float glow = 1.0 - smoothstep(0.08, 0.3, starShape);
                float verticalPeak =
                    exp(-p.x * 42.0) *
                    exp(-p.y * 6.5) * 0.55;
                float horizontalPeak =
                    exp(-p.y * 42.0) *
                    exp(-p.x * 6.5) * 0.55;
                return core + glow * 0.16 + verticalPeak + horizontalPeak;
            }

            float layeredStars(vec2 uv, float cellScale, float threshold, float sizeMultiplier) {
                vec2 gridUv = uv * cellScale;
                vec2 baseCell = floor(gridUv);
                float stars = 0.0;

                for (int offsetX = -1; offsetX <= 1; offsetX++) {
                    for (int offsetY = -1; offsetY <= 1; offsetY++) {
                        vec2 cell = baseCell + vec2(float(offsetX), float(offsetY));
                        vec2 starOffset = vec2(
                            hash21(cell + 4.31),
                            hash21(cell + 9.17)
                        ) - 0.5;
                        float starMask = step(threshold, hash21(cell + 17.31));
                        vec2 starCenter = (cell + 0.5 + starOffset * 0.55) / cellScale;
                        vec2 starUv = (uv - starCenter) * sizeMultiplier;
                        float twinklePhase = sin(uTime * (1.55 + hash21(cell + 23.1) * 1.1) + hash21(cell) * 6.28318);
                        float twinkle = 0.08 + 0.92 * (0.5 + 0.5 * twinklePhase);
                        stars += starSparkle(starUv) * starMask * twinkle;
                    }
                }

                return stars;
            }

            void main() {
                vec2 screenUv = gl_FragCoord.xy / uResolution;
                vec2 centeredUv = screenUv - 0.5;
                centeredUv.x *= uResolution.x / max(uResolution.y, 0.0001);

                float time = uTime * 0.045;
                vec2 flowUv = centeredUv * 6.8;
                vec2 warp = vec2(
                    fbm(centeredUv * 3.0 + vec2(time * 0.4, -time * 0.25)),
                    fbm(centeredUv * 3.0 + vec2(3.1 - time * 0.28, 1.4 + time * 0.32))
                ) - 0.5;

                float nebulaA = fbm(rotate2D(time * 0.18) * (flowUv + warp * 0.9));
                float nebulaB = fbm(rotate2D(-time * 0.14) * (flowUv * 1.45 - warp * 0.7));
                float nebula = smoothstep(0.4, 0.88, nebulaA * 0.68 + nebulaB * 0.32);
                nebula = pow(nebula, 1.25);

                float ribbons = 1.0 - smoothstep(
                    0.03,
                    0.085,
                    abs(sin(centeredUv.y * 7.5 + centeredUv.x * 2.2 - time * 2.4 + nebulaB * 2.2))
                );
                ribbons *= smoothstep(0.5, 0.9, nebula);

                float mist = pow(fbm(centeredUv * 16.0 - vec2(time * 0.65, -time * 0.3)), 3.4) * 0.08;
                float glow = exp(-length(centeredUv - vec2(-0.08, 0.02)) * 4.4) * 0.26;
                float starsLarge = layeredStars(centeredUv, 34.0, 0.965, 73.3333333333);
                float starsSmall = layeredStars(centeredUv + vec2(0.17, -0.09), 58.0, 0.982, 126.6666666667) * 0.75;
                float stars = starsLarge + starsSmall;

                vec3 deepBlack = vec3(0.0, 0.0, 0.0);
                vec3 deepBlue = vec3(0.00, 0.00, 0.00);
                vec3 brightBlue = vec3(0.0, 0.0, 0.01);
                vec3 cobaltBlue = vec3(0.01, 0.01, 0.1);

                vec3 color = deepBlack;
                color += mix(deepBlue, cobaltBlue, nebulaB) * nebula * 0.5;
                color += brightBlue * ribbons * 0.9;
                color += deepBlue * mist * 0.85;
                color += vec3(0.18, 0.34, 0.88) * glow * 0.22;
                color += vec3(0.82, 0.9, 1.0) * stars * 2.45;

                gl_FragColor = vec4(color, uOpacity);
            }
        `,
    })
}

export function createEndCardBackgroundMaterial(backgroundTexture: Texture) {
    const textureWidth = backgroundTexture.image?.width ?? 1
    const textureHeight = backgroundTexture.image?.height ?? 1

    return new ShaderMaterial({
        side: FrontSide,
        transparent: true,
        toneMapped: false,
        uniforms: {
            uBaseMap: { value: backgroundTexture },
            uUseBaseMap: { value: 1 },
            uResolution: { value: new Vector2(1, 1) },
            uMapSize: { value: new Vector2(textureWidth, textureHeight) },
            uScreenScale: { value: endCardBackgroundScreenScale },
            uOffset: { value: new Vector2(endCardBackgroundOffsetX, endCardBackgroundOffsetYA) },
            uOpacity: { value: 1 },
        },
        vertexShader: `
            void main() {
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform sampler2D uBaseMap;
            uniform int uUseBaseMap;
            uniform vec2 uResolution;
            uniform vec2 uMapSize;
            uniform float uScreenScale;
            uniform vec2 uOffset;
            uniform float uOpacity;

            vec2 coverUv(vec2 screenUv, vec2 screenSize, vec2 textureSize) {
                float screenAspect = screenSize.x / max(screenSize.y, 0.0001);
                float textureAspect = textureSize.x / max(textureSize.y, 0.0001);
                vec2 uv = screenUv;
                if (screenAspect > textureAspect) {
                    float scale = textureAspect / screenAspect;
                    uv.y = (uv.y - 0.5) * scale + 0.5;
                } else {
                    float scale = screenAspect / textureAspect;
                    uv.x = (uv.x - 0.5) * scale + 0.5;
                }
                return uv;
            }

            void main() {
                vec2 screenUv = gl_FragCoord.xy / uResolution;
                screenUv.y = 1.0 - screenUv.y;
                screenUv = (screenUv - 0.5) / uScreenScale + 0.5;
                vec2 textureUv = coverUv(screenUv, uResolution, uMapSize);
                textureUv = (textureUv - 0.5) + 0.5 + uOffset;
                textureUv = fract(textureUv);
                vec4 baseSample = uUseBaseMap == 1 ? texture2D(uBaseMap, textureUv) : vec4(1.0);
                float alpha = baseSample.a * uOpacity;
                if (alpha <= 0.001) {
                    discard;
                }
                vec3 color = baseSample.rgb;
                gl_FragColor = vec4(color, alpha);
            }
        `,
    })
}

export function createEndCardFrameMaterial(
    frameTexture: Texture,
    normalMap: Texture | null,
    viewAngleOffset = new Vector2(0, 0),
) {
    return new ShaderMaterial({
        side: FrontSide,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        toneMapped: false,
        uniforms: {
            uBaseMap: { value: frameTexture },
            uNormalMap: { value: normalMap },
            uUseBaseMap: { value: 1 },
            uUseNormalMap: { value: normalMap ? 1 : 0 },
            uViewAngleOffset: { value: viewAngleOffset.clone() },
            uTime: { value: 0 },
            uOpacity: { value: 1 },
        },
        vertexShader: `
            varying vec2 vUv;
            varying vec3 vNormalView;
            varying vec3 vViewDir;

            void main() {
                vUv = uv;
                vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
                vNormalView = normalize(normalMatrix * normal);
                vViewDir = normalize(-viewPosition.xyz);
                gl_Position = projectionMatrix * viewPosition;
            }
        `,
        fragmentShader: `
            uniform sampler2D uBaseMap;
            uniform sampler2D uNormalMap;
            uniform int uUseBaseMap;
            uniform int uUseNormalMap;
            uniform vec2 uViewAngleOffset;
            uniform float uTime;
            uniform float uOpacity;

            varying vec2 vUv;
            varying vec3 vNormalView;
            varying vec3 vViewDir;

            float sat(float value) {
                return clamp(value, 0.0, 1.0);
            }

            float hash21(vec2 p) {
                p = fract(p * vec2(123.34, 456.21));
                p += dot(p, p + 45.32);
                return fract(p.x * p.y);
            }

            vec3 hueToRgb(float hue) {
                vec3 rgb = abs(mod(hue * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0;
                return clamp(rgb, 0.0, 1.0);
            }

            float foilLinePattern(vec2 uv) {
                float linesA = sin(uv.x * 220.0 + uv.y * 38.0);
                float linesB = sin(uv.x * -160.0 + uv.y * 112.0);
                float linesC = sin((uv.x + uv.y) * 310.0);
                return linesA * 0.45 + linesB * 0.35 + linesC * 0.2;
            }

            float sparklePattern(vec2 uv) {
                vec2 gridUv = uv * 28.0;
                vec2 cell = floor(gridUv);
                vec2 localUv = fract(gridUv) - 0.5;
                float sparkleMask = step(0.89, hash21(cell + 5.7));
                float dist = length(localUv);
                float sparkle = 1.0 - smoothstep(0.04, 0.22, dist);
                return sparkle * sparkleMask;
            }

            void main() {
                vec4 baseSample = uUseBaseMap == 1
                    ? texture2D(uBaseMap, vUv)
                    : vec4(1.0);
                float alphaSample = baseSample.a;

                vec3 sampledNormal = uUseNormalMap == 1
                    ? texture2D(uNormalMap, vUv).xyz * 2.0 - 1.0
                    : vec3(0.0, 0.0, 1.0);
                sampledNormal.xy *= 0.45;
                sampledNormal.z = max(sampledNormal.z, 0.35);
                sampledNormal = normalize(sampledNormal);

                vec3 normalView = normalize(vNormalView);
                vec3 viewDir = normalize(vViewDir);
                vec3 detailNormalView = normalize(vec3(
                    normalView.xy + sampledNormal.xy * 0.55,
                    max(normalView.z * sampledNormal.z, 0.0001)
                ));
                vec3 reflectedView = reflect(-viewDir, detailNormalView);
                vec2 adjustedView = reflectedView.xy + uViewAngleOffset;

                float edgeAmount = pow(1.0 - abs(dot(detailNormalView, viewDir)), 0.95);

                vec2 uvCentered = vUv - 0.5;
                vec2 sweepDirection = normalize(adjustedView + vec2(0.0001, 0.0001));
                float sweep = dot(uvCentered, sweepDirection);
                float sweepBand = 1.0 - smoothstep(
                    0.045,
                    0.3,
                    abs(sweep + reflectedView.z * 0.1 + sampledNormal.x * 0.08 + dot(uViewAngleOffset, vec2(0.18, -0.12)))
                );

                float foilPattern = foilLinePattern(vUv + sampledNormal.xy * 0.05);
                float foilMask = 0.5 + 0.5 * foilPattern;
                foilMask = pow(foilMask, 1.15);

                float finePattern = 0.5 + 0.5 * sin(
                    vUv.y * 540.0 +
                    vUv.x * 80.0 +
                    adjustedView.x * 7.0 +
                    sampledNormal.y * 18.0
                );
                float sparkle = sparklePattern(
                    vUv +
                    adjustedView * 0.08 +
                    sampledNormal.xy * 0.12 +
                    uTime * 0.01
                );
                float baseLuma = dot(baseSample.rgb, vec3(0.299, 0.587, 0.114));

                float hologramMask = sat(edgeAmount * 0.72 + sweepBand * 0.58 + 0.08);
                hologramMask *= mix(0.68, 1.0, foilMask);
                hologramMask *= mix(0.92, 1.08, finePattern);
                hologramMask *= mix(0.78, 1.0, baseLuma);

                float hue =
                    vUv.x * 0.55 +
                    vUv.y * 0.18 +
                    adjustedView.x * 0.16 -
                    adjustedView.y * 0.12 +
                    sampledNormal.x * 0.1 +
                    sampledNormal.y * 0.06 +
                    sweep * 0.14;
                vec3 rainbow = hueToRgb(fract(hue));
                rainbow = pow(rainbow, vec3(0.7));
                rainbow = mix(vec3(0.0, 1.0, 1.0), rainbow, 0.88);

                vec3 foilColor = rainbow * (0.82 + foilMask * 0.78 + sparkle * 1.15);
                vec3 color = foilColor * hologramMask;

                float alpha = alphaSample * uOpacity * hologramMask * 0.9;
                if (alpha <= 0.001) {
                    discard;
                }
                gl_FragColor = vec4(color, alpha);
            }
        `,
    })
}

export function createEndCardStreaksMaterial(palette: EndCardStreaksPalette) {
    return new ShaderMaterial({
        side: FrontSide,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        toneMapped: false,
        uniforms: {
            uTime: { value: 0 },
            uOpacity: { value: 1 },
            uSpeed: { value: endCardStreaksSpeed },
            uDensity: { value: endCardStreaksDensity },
            uWidthCenter: { value: endCardStreaksWidthCenter },
            uWidthEdge: { value: endCardStreaksWidthEdge },
            uEdgeFadeStart: { value: endCardStreaksEdgeFadeStart },
            uEdgeFadeEnd: { value: endCardStreaksEdgeFadeEnd },
            uEdgeNoise: { value: endCardStreaksEdgeNoise },
            uGradientMid: { value: endCardStreaksGradientMid },
            uGradientOuterStart: { value: endCardStreaksGradientOuterStart },
            uInnerColor: { value: palette.inner.clone() },
            uMiddleColor: { value: palette.middle.clone() },
            uOuterColor: { value: palette.outer.clone() },
        },
        vertexShader: `
            varying vec2 vUv;

            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform float uTime;
            uniform float uOpacity;
            uniform float uSpeed;
            uniform float uDensity;
            uniform float uWidthCenter;
            uniform float uWidthEdge;
            uniform float uEdgeFadeStart;
            uniform float uEdgeFadeEnd;
            uniform float uEdgeNoise;
            uniform float uGradientMid;
            uniform float uGradientOuterStart;
            uniform vec3 uInnerColor;
            uniform vec3 uMiddleColor;
            uniform vec3 uOuterColor;

            varying vec2 vUv;

            float hash21(vec2 p) {
                p = fract(p * vec2(123.34, 456.21));
                p += dot(p, p + 45.32);
                return fract(p.x * p.y);
            }

            float radialStreakLayer(vec2 uv, float radialScale, float angularScale, float speed, float seed) {
                float angle = atan(uv.y, uv.x);
                float radius = length(uv);
                float clampedRadius = clamp(radius, 0.0, 1.0);
                float radialFlow = fract(radius * radialScale - uTime * speed * uSpeed + seed);
                float width = mix(uWidthCenter, uWidthEdge, clampedRadius);
                float radialPulse = smoothstep(0.0, width, radialFlow) * (1.0 - smoothstep(width, width + 0.46, radialFlow));
                float angularNoise = sin(angle * angularScale * uDensity + seed * 6.28318 + radius * (8.0 + uDensity * 2.0));
                float angularThreshold = mix(0.28, 0.72, clampedRadius);
                float angularMask = smoothstep(angularThreshold, 0.98, angularNoise);
                return radialPulse * angularMask;
            }

            void main() {
                vec2 uv = vUv * 2.0 - 1.0;
                float radius = length(uv);
                float clampedRadius = clamp(radius, 0.0, 1.0);
                float edgeNoise = hash21(normalize(uv + vec2(0.0001)) * 9.0 + clampedRadius * 13.0 + floor(uTime * 0.5) * 0.17);
                float edgeFade = 1.0 - smoothstep(uEdgeFadeStart, uEdgeFadeEnd, radius + (edgeNoise - 0.5) * uEdgeNoise);
                float centerFade = smoothstep(0.02, 0.18, radius);
                float mask = edgeFade * centerFade;

                float streaks = 0.0;
                streaks += radialStreakLayer(uv, 5.5, 32.0, 3.4, 0.13);
                streaks += radialStreakLayer(uv, 7.4, 44.0, 4.2, 0.29) * 0.92;
                streaks += radialStreakLayer(uv, 9.8, 58.0, 5.1, 0.41) * 0.8;
                streaks += radialStreakLayer(uv, 12.6, 76.0, 6.2, 0.57) * 0.68;
                streaks += radialStreakLayer(uv, 15.2, 92.0, 7.3, 0.73) * 0.52;

                vec3 radialGradient = mix(uInnerColor, uMiddleColor, smoothstep(0.0, uGradientMid, clampedRadius));
                radialGradient = mix(radialGradient, uOuterColor, smoothstep(uGradientOuterStart, 1.0, clampedRadius));

                float softGlow = (1.0 - smoothstep(0.08, 0.5, radius)) * 0.045;
                float alpha = (streaks * mask + softGlow) * uOpacity;
                vec3 color = radialGradient * (streaks * 1.35 + softGlow * 0.45);

                gl_FragColor = vec4(color, alpha);
            }
        `,
    })
}
